<template>
    <div class="page" ref="pdfContent" id="pdfContent">
        <div class="row">
            <div class="col-lg-12 col-sm-12">
                <basic-chart :title="$t('Team Trend')" :data="overAllTrendDataset"
                             :badge-value="overAllAverage"/>
            </div>
        </div>
        <div class="row">
            <layout-wrapper v-for="(dataset, i) in teamAnswersDatasets" :key="`pdfTeamAnswersDataset_${i}`"
                            :length="teamAnswersDatasets.length" :print="true">
                <template slot="body">
                    <basic-chart :title="dataset.title | languageString" :data="dataset" pdfPrint
                                 :badge-value="dataset.average"/>
                </template>
            </layout-wrapper>
        </div>
        <div class="pr-3 pl-3" style="padding-top: 50px;">
            <h4 class="mt-3 mb-2"><b>{{$t('Team members')}}</b></h4>
            <div class="row pb-3" v-for="(dataset, i) in teamUserAccumulations">
                <div class="col-sm-6 mb-1 mt-5" v-if="!dataset.allQueries">
                    <h5 class="m-0">{{dataset.user | formatDisplayName}}</h5>
                    <div class="form-text mb-3">
                        <span class="d-inline">{{`${$t('Most recent data')}: `}}</span>
                        <span class="d-inline">{{ dataset.lastQuery.query.localDateLabel }}</span>
                    </div>
                </div>
                <div class="col-sm-6 mb-0 mt-5" v-else>
                    <h5 class="m-0"><b>{{dataset.user | formatDisplayName}}</b></h5>
                </div>
                <div class="col-sm-12 p-0" v-if="dataset.allQueries">
                  <div class="col-sm-12">
                      <hr style="border-top: 1px solid rgba(0,0,0,.3);" class="hr-margin" />
                  </div>
                </div>

                <div class="col-sm-6 mt-5" v-if="!dataset.allQueries">
                    <div class="pull-right">
                        <status-indicator v-for="(answers, index) in dataset.answers"
                                          class="mr-1"
                                          :value="answers.answer"
                                          :key="index"
                                          :precision="1"
                                          :trend="answers.trend"/>
                    </div>
                </div>

                <div class="col-sm-12 mb-1" v-if="!dataset.allQueries" style="page-break-inside: avoid;">
                    <div class="row" v-for="question in dataset.grouped.text">
                        <div class="col-sm-4">
                            <p>{{question.title | languageString}}</p>
                        </div>
                        <div class="col-sm-8 fix-line-breaks">
                            <p>{{question.answer}}</p>
                        </div>
                    </div>
                    <div class="col-sm-12 p-0">
                      <hr style="border-top: 1px solid rgba(0,0,0,.3);" class="hr-margin" />
                    </div>
                </div>
                <div class="col-sm-12 mb-2" v-for="(oneQuery, i) in dataset.allQueries" v-else style="page-break-inside: avoid !important; display:block;">
                  <div class="col-sm-12 pl-0" style="page-break-inside: avoid !important;">
                    <table style="width: 100%" class="mb-3">
                      <tr>
                        <td>
                          <div class="form-text mb-0 mt-0">
                              <p class="d-inline text-bold fs-14">{{`${$t('Query')}: `}}</p>
                              <p v-if="oneQuery.query" class="d-inline text-bold fs-14">{{ oneQuery.query.localDateLabel }}</p>
                          </div>
                        </td>
                        <td>
                          <div class="pull-right">
                              <status-indicator v-for="(answers, index) in filterSliders(oneQuery.answers)"
                                                class="mr-1"
                                                :value="answers.answer"
                                                :key="index"
                                                :precision="1"
                                                :trend="answers.trend"/>
                          </div>
                        </td>
                      </tr>
                    </table>
                    
                  </div>
                  <div class="col-sm-12 mb-0">
                    <div class="row" v-if="!answerAllEmpty(oneQuery.answers)" v-for="question in filterText(oneQuery.answers)">
                        <div class="col-sm-4 pl-0">
                            <p>{{question.title | languageString}}</p>
                        </div>
                        <div class="col-sm-8 fix-line-breaks">
                            <p>{{question.answer}}</p>
                        </div>
                    </div>
                  </div>
                  <hr v-if="i + 1 < dataset.allQueries.length" style="border-top: 1px solid rgba(0,0,0,.1); margin-top: -10px !important" class="m-0"/>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
  import BasicChart from '../../Dashboard/Components/BasicChart'
  import StatusIndicator from '../../Dashboard/Components/StatusIndicator'
  import LayoutWrapper from '../../UIComponents/LayoutWrapper'
  import axios from 'axios'
  import groupBy from 'lodash/groupBy'
  import get from 'lodash/get'
  import map from 'lodash/map'
  import isEmpty from 'lodash/isEmpty'
  import filter from 'lodash/filter'
  import moment from 'moment-timezone'

  export default {
    name: 'TeamPdf',
    components: {
      BasicChart,
      StatusIndicator,
      LayoutWrapper
    },
    mounted () {
      this.init()
    },
    data () {
      return {
        data: null,
        createdAt: moment().format('L'),
        monthsOptions: {
          1: 'Last 1 month',
          2: 'Last 2 months',
          3: 'Last 3 months',
          6: 'Last 6 months',
          9: 'Last 9 months',
          12: 'Last 12 months',
          15: 'Last 15 months',
          18: 'Last 18 months'
        },
        title: ''
      }
    },
    computed: {
      team: function () {
        return this.data ? this.data.team : null
      },
      overAllAverage: function () {
        return get(this.data, 'overAllTrend.average')
      },
      overAllTrendDataset: function () {
        const overAllTrend = get(this.data, 'overAllTrend')
        if (overAllTrend) {
          const { labels, series } = overAllTrend
          if (labels.length === series.length && labels.length < 100) {
            return { labels, series }
          }
        }
        return {}
      },
      teamAnswersDatasets: function () {
        return Object.values(get(this.data, 'answers', {}))
      },
      teamUserAccumulations: function () {
        const items = Object.values(get(this.data, 'userAccumulations', {})).sort(this.sortUsers)
        if (!isEmpty(items)) {
          return map(items, item => {
            const questions = get(item, 'lastQuery.query.questionnaireObj.questions')
            const answers = new Map(item.lastQuery.answers.map(({ question, answer }) => ([question, answer])))
            const filteredQuestions = filter(questions, question => answers.get(question._id))
            filteredQuestions.map(question => Object.assign(question, { answer: answers.get(question._id) }))

            return Object.assign(item, { grouped: groupBy(filteredQuestions, 'type') })
          })
        }
        return items
      }
    },
    methods: {
      async init () {
        const { qid, tid, months } = this.$route.params
        this.title = this.$t(this.monthsOptions[this.$route.params.months])
        if (this.$route.query.start && this.$route.query.end) {
          this.title = `${moment.tz(this.$route.query.start, 'YYYY-MM-DD').format('YYYY-MM-DD')} ${this.$t('to')} ${moment.tz(this.$route.query.end, 'YYYY-MM-DD').format('YYYY-MM-DD')}`
        }

        try {
          const response = await axios.get(`/api/myTeam/questionnaires/${qid}/team/${tid}/pdfexport`, { params: { months, start: this.$route.query.start, end: this.$route.query.end, lang: this.$route.query.lang } })
          this.data = response.data
        } catch (e) {
          this.$notify({ message: e.message, type: 'danger' })
        }
      },

      filterSliders (answers) {
        if (!answers) return []
        return answers.filter( x => x.type === 'slider')
      },

      filterText (answers) {
        if (!answers) return []
        return answers.filter( x => x.type === 'text')
      },
      sortUsers (a, b) {
        const an = `${a.user.firstName} ${a.user.lastName}`
        const bn = `${b.user.firstName} ${b.user.lastName}`
        if (an < bn) {
          return -1
        } else if (an > bn) {
          return 1
        } else {
          return 0
        }
      },
      answerAllEmpty(answers) {
        if (!answers) {
          return true
        }

        for (const answer of answers) {
          if (answer.answer && answer.answer.length > 0) {
            return false
          }
        }

        return true
      }
    }
  }
</script>

<style lang="scss">
    .page {
        width: 297mm;
        height: 210mm;
    }

    .fix-line-breaks {
      word-break: break-word;
      white-space: pre-wrap;
    }

    .text-bold {
      font-weight: bold;
    }

    .fs-14 {
      font-size: 14px;
    }

    .hr-margin {
      margin-top: 8px;
      margin-bottom: 8px;
    }

    @media screen {}

    @media print {
        @page {
            size: 'A4';
        }
    }
</style>